import i18n from "@/lib/i18n";

const WORKOS_CODE_VERIFIER_KEY = "workos:code-verifier";
const WORKOS_REFRESH_TOKEN_KEY = "workos:refresh-token";
const WORKOS_ORGANIZATION_KEY = "workos_organization_id";
const WORKOS_AUTH_STARTED_AT_KEY = "l9:workos-auth-started-at";
const WORKOS_CALLBACK_PATH = "/auth/callback";
const PKCE_FLOW_MAX_AGE_MS = 20 * 60 * 1000;

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export interface WorkOSImpersonator {
  email: string;
  reason: string | null;
}

export interface WorkOSImpersonationExchangeResponse {
  refresh_token: string;
  organization_id: string | null;
  authentication_method: "Impersonation";
  impersonator: WorkOSImpersonator;
  user: {
    email: string;
  };
}

function browserSessionStorage(): StorageLike | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function browserLocalStorage(): StorageLike | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function currentHref(): string | null {
  try {
    return typeof window === "undefined" ? null : window.location.href;
  } catch {
    return null;
  }
}

function parseUrl(href: string): URL {
  return new URL(href, "http://localhost");
}

function isSupportedCallbackPath(pathname: string): boolean {
  const normalized = pathname !== "/" ? pathname.replace(/\/+$/, "") : pathname;
  return normalized === "/" || normalized === WORKOS_CALLBACK_PATH;
}

function workOSOrganizationKey(clientId: string): string {
  return `workos-org-id:${clientId}`;
}

function workOSRefreshTokenKey(clientId: string): string {
  return `${WORKOS_REFRESH_TOKEN_KEY}:${clientId}`;
}

export function markWorkOSAuthorizationStarted(
  storage: StorageLike | null = browserSessionStorage(),
  now = Date.now(),
): void {
  if (!storage) return;
  try {
    storage.setItem(WORKOS_AUTH_STARTED_AT_KEY, String(now));
  } catch {
    // AuthKit also needs sessionStorage for PKCE, so it will surface the
    // underlying storage failure when it attempts to start authentication.
  }
}

export function clearWorkOSAuthorizationMarker(
  storage: StorageLike | null = browserSessionStorage(),
): void {
  if (!storage) return;
  try {
    storage.removeItem(WORKOS_AUTH_STARTED_AT_KEY);
  } catch {
    // Best-effort cleanup only.
  }
}

export function clearWorkOSPendingAuthorization(
  storage: StorageLike | null = browserSessionStorage(),
): void {
  if (!storage) return;
  try {
    storage.removeItem(WORKOS_AUTH_STARTED_AT_KEY);
    storage.removeItem(WORKOS_CODE_VERIFIER_KEY);
  } catch {
    // Best-effort cleanup only.
  }
}

export function hasActiveWorkOSPkceFlow(
  storage: StorageLike | null = browserSessionStorage(),
  now = Date.now(),
): boolean {
  if (!storage) return false;
  try {
    const verifier = storage.getItem(WORKOS_CODE_VERIFIER_KEY);
    const startedAt = Number(storage.getItem(WORKOS_AUTH_STARTED_AT_KEY));
    const age = now - startedAt;
    return Boolean(verifier)
      && Number.isFinite(startedAt)
      && startedAt > 0
      && age >= 0
      && age <= PKCE_FLOW_MAX_AGE_MS;
  } catch {
    return false;
  }
}

/**
 * Dashboard impersonation codes arrive without an app-created PKCE attempt.
 * Restrict interception to the WorkOS callback/root paths so OAuth callbacks
 * for other integrations (for example /ms365/callback) remain untouched.
 */
export function shouldHandleWorkOSDashboardCallback(
  href: string | null = currentHref(),
  storage: StorageLike | null = browserSessionStorage(),
  now = Date.now(),
): boolean {
  if (!href) return false;
  const url = parseUrl(href);
  return isSupportedCallbackPath(url.pathname)
    && url.searchParams.has("code")
    && !hasActiveWorkOSPkceFlow(storage, now);
}

/**
 * Use the dedicated callback for new sign-ins. If a PKCE callback is already
 * in flight on the legacy root path, retain that path so AuthKit can finish it.
 */
export function getWorkOSRedirectUri(
  href: string | null = currentHref(),
  storage: StorageLike | null = browserSessionStorage(),
  now = Date.now(),
): string {
  if (!href) return WORKOS_CALLBACK_PATH;
  const url = parseUrl(href);
  if (
    isSupportedCallbackPath(url.pathname)
    && url.searchParams.has("code")
    && hasActiveWorkOSPkceFlow(storage, now)
  ) {
    const callbackPath = url.pathname === "/"
      ? "/"
      : WORKOS_CALLBACK_PATH;
    return new URL(callbackPath, url.origin).toString();
  }
  return new URL(WORKOS_CALLBACK_PATH, url.origin).toString();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function deserializeImpersonationResponse(
  value: unknown,
): WorkOSImpersonationExchangeResponse {
  if (!isRecord(value)) {
    throw new Error(i18n.t("auth.errors.invalidImpersonationResponse"));
  }

  const impersonator = value.impersonator;
  const user = value.user;
  const organizationId = value.organization_id;
  if (
    typeof value.refresh_token !== "string"
    || !value.refresh_token
    || value.authentication_method !== "Impersonation"
    || !isRecord(impersonator)
    || typeof impersonator.email !== "string"
    || (impersonator.reason !== null && typeof impersonator.reason !== "string")
    || !isRecord(user)
    || typeof user.email !== "string"
    || (organizationId !== null && typeof organizationId !== "string")
  ) {
    throw new Error(i18n.t("auth.errors.invalidImpersonationResponse"));
  }

  return value as unknown as WorkOSImpersonationExchangeResponse;
}

export async function exchangeWorkOSDashboardCode(
  code: string,
  fetchImpl: typeof fetch = fetch,
): Promise<WorkOSImpersonationExchangeResponse> {
  const response = await fetchImpl("/api/auth/workos/callback", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ code }),
  });

  const body = await response.json().catch(() => null) as unknown;
  if (!response.ok) {
    const message = isRecord(body) && typeof body.error === "string"
      ? body.error
      : "The impersonation code could not be exchanged";
    throw new Error(message);
  }

  return deserializeImpersonationResponse(body);
}

export function persistWorkOSImpersonationSession(
  response: WorkOSImpersonationExchangeResponse,
  clientId: string,
  localStorage: StorageLike | null = browserLocalStorage(),
  sessionStorage: StorageLike | null = browserSessionStorage(),
): void {
  if (!clientId) {
    throw new Error(i18n.t("auth.errors.clientIdMissing"));
  }
  if (!localStorage) {
    throw new Error(i18n.t("auth.errors.storageUnavailable"));
  }

  // AuthKit's devMode persists only the refresh token. A full navigation
  // below lets AuthKit refresh it and repopulate its in-memory user, access
  // token, authentication method, and impersonator state normally.
  localStorage.setItem(workOSRefreshTokenKey(clientId), response.refresh_token);

  if (sessionStorage) {
    try {
      sessionStorage.removeItem(WORKOS_ORGANIZATION_KEY);
      if (response.organization_id) {
        sessionStorage.setItem(
          workOSOrganizationKey(clientId),
          response.organization_id,
        );
      } else {
        sessionStorage.removeItem(workOSOrganizationKey(clientId));
      }
      sessionStorage.removeItem(WORKOS_CODE_VERIFIER_KEY);
      sessionStorage.removeItem(WORKOS_AUTH_STARTED_AT_KEY);
    } catch {
      // The refresh token is already safely installed. AuthKit can still
      // hydrate it; these per-tab values are only organization/PKCE hints.
    }
  }
}

export async function completeWorkOSDashboardCallback({
  code,
  clientId,
  fetchImpl = fetch,
  localStorage = browserLocalStorage(),
  sessionStorage = browserSessionStorage(),
}: {
  code: string;
  clientId: string;
  fetchImpl?: typeof fetch;
  localStorage?: StorageLike | null;
  sessionStorage?: StorageLike | null;
}): Promise<WorkOSImpersonationExchangeResponse> {
  if (!clientId) {
    throw new Error(i18n.t("auth.errors.clientIdMissing"));
  }
  if (!localStorage) {
    throw new Error(i18n.t("auth.errors.storageUnavailable"));
  }

  const response = await exchangeWorkOSDashboardCode(code, fetchImpl);
  persistWorkOSImpersonationSession(
    response,
    clientId,
    localStorage,
    sessionStorage,
  );
  return response;
}
